import { FLAG_REGISTRY } from './registry.js';
import type { FlagDefinition } from './types.js';

/**
 * `flags_schema_version` (ADR-0022 §1) — a short, stable fingerprint of the
 * compiled flag registry's SHAPE. It changes whenever a flag is added/removed,
 * a default or allowed-value set changes, or a type/annotation changes; it does
 * NOT change with DB values (those are deltas, not schema).
 *
 * The heartbeat carries this so a mixed-CLI fleet can be reasoned about: the
 * admin UI (ENG-6252) shows which hosts run a manager old enough that a newly
 * added flag won't be honoured yet. A host echoing an older schema version
 * simply hasn't shipped the new registry — its evaluation still fails safe to
 * whatever defaults its binary compiled in.
 *
 * Computed with a pure FNV-1a hash so this module stays browser-safe (no
 * `node:crypto`); the core barrel is imported by the webapp client bundle.
 */

/**
 * Stable structural projection of a flag — order-independent within a key.
 *
 * Deliberately EXCLUDES `envVar`, `since` and `newOrgOverride`. Each is real
 * registry data, but none of them changes what a host's compiled evaluator
 * resolves a flag to, and this fingerprint exists so a mixed-CLI fleet can be
 * reasoned about. `newOrgOverride` (ENG-9629) is the strongest case of the
 * three: it is consumed once, control-plane-side, at organization creation, and
 * no host reads it at all — so declaring one must not roll the version for every
 * manager in the fleet. See the ENG-9629 pin in the core feature-flags tests.
 */
function projectDefinition(definition: FlagDefinition): string {
  const parts: string[] = [
    `k=${definition.key}`,
    `t=${definition.flagType}`,
    `d=${String(definition.defaultValue)}`,
    `p=${definition.public === true ? 1 : 0}`,
    `s=${definition.sensitive === true ? 1 : 0}`,
  ];
  if (definition.flagType === 'enum') {
    // Sort allowed values so member reordering alone is not a schema change.
    parts.push(`a=${[...definition.allowedValues].sort().join(',')}`);
  }
  return parts.join('|');
}

/** FNV-1a 32-bit, returned as 8 lowercase hex chars. */
function fnv1aHex(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // hash *= 16777619, kept in 32-bit unsigned space.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function computeFlagsSchemaVersion(): string {
  // Sort by key so registry declaration order never moves the version.
  const canonical = [...FLAG_REGISTRY]
    .map(projectDefinition)
    .sort()
    .join('\n');
  return `v1:${fnv1aHex(canonical)}`;
}

/**
 * The schema version for the registry compiled into this build. Computed once
 * at module load (the registry is static `as const`).
 */
export const FLAGS_SCHEMA_VERSION: string = computeFlagsSchemaVersion();

/**
 * Test-only view of the per-flag projection, so the ENG-9629 exclusion of
 * `newOrgOverride` can be asserted directly rather than inferred from the hash
 * of the whole registry (which would also pass if the projection changed and the
 * collision happened to be benign).
 */
export const __projectDefinitionForTests = projectDefinition;
